import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	VStack,
	Text,
	Link,
	Icon,
	useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FaArrowLeft } from 'react-icons/fa';
import { resetPassword } from '../../../Api/authApi';
import {
	authFormInputProps,
	authFormLabelProps,
	authFormLinkProps,
	authFormMutedTextProps,
	authFormPrimaryButtonProps,
	authFormShellProps,
} from './authFormTheme';

const ResetPassword = ({ showTitle = true }) => {
	const { token } = useParams();
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const toast = useToast();

	useEffect(() => {
		if (!token) {
			toast({
				title: 'Invalid reset link',
				description: 'Invalid reset link. Please request a new password reset.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	}, [token, toast]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		if (!token) {
			toast({
				title: 'Invalid reset link',
				description: 'Invalid reset link. Please request a new password reset.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setIsLoading(false);
			return;
		}

		if (newPassword !== confirmPassword) {
			toast({
				title: 'Password mismatch',
				description: 'Passwords do not match',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setIsLoading(false);
			return;
		}

		if (newPassword.length < 6) {
			toast({
				title: 'Weak password',
				description: 'Password must be at least 6 characters long',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setIsLoading(false);
			return;
		}

		try {
			const data = await resetPassword(token, newPassword);
			toast({
				title: 'Password reset',
				description:
					data.msg || 'Password reset successfully! Redirecting to login...',
				status: 'success',
				duration: 5000,
				isClosable: true,
			});
			setTimeout(() => {
				navigate('/auth');
			}, 2000);
		} catch (err) {
			console.error('Password reset error:', err);
			const errorMessage =
				err.response?.data?.msg ||
				err.message ||
				'Failed to reset password. Please try again.';
			toast({
				title: 'Reset failed',
				description: errorMessage,
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setIsLoading(false);
		}
	};

	return (
		<Box {...authFormShellProps} as='form' onSubmit={handleSubmit}>
			<VStack spacing={5} align='stretch'>
				<Button
					variant='ghost'
					size='sm'
					alignSelf='flex-start'
					color='gray.600'
					leftIcon={<Icon as={FaArrowLeft} />}
					onClick={() => navigate('/auth')}
					_hover={{ bg: 'gray.100', color: 'gray.800' }}>
					Back to sign in
				</Button>

				{showTitle && (
					<>
						<Text fontSize='xl' fontWeight='bold' color='gray.800' textAlign='center'>
							Set a new password
						</Text>
						<Text color='gray.600' fontSize='md' textAlign='center' mb={0}>
							Choose a strong password for your account.
						</Text>
					</>
				)}

				<FormControl isRequired>
					<FormLabel {...authFormLabelProps}>New password</FormLabel>
					<InputGroup>
						<Input
							type={showNew ? 'text' : 'password'}
							placeholder='Enter new password'
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							minLength={6}
							pr='3rem'
							{...authFormInputProps}
						/>
						<InputRightElement h='full'>
							<Button
								size='sm'
								variant='ghost'
								color='gray.500'
								onClick={() => setShowNew((s) => !s)}
								tabIndex={-1}
								aria-label={showNew ? 'Hide password' : 'Show password'}>
								{showNew ? <ViewOffIcon /> : <ViewIcon />}
							</Button>
						</InputRightElement>
					</InputGroup>
				</FormControl>

				<FormControl isRequired>
					<FormLabel {...authFormLabelProps}>Confirm password</FormLabel>
					<InputGroup>
						<Input
							type={showConfirm ? 'text' : 'password'}
							placeholder='Confirm new password'
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							minLength={6}
							pr='3rem'
							{...authFormInputProps}
						/>
						<InputRightElement h='full'>
							<Button
								size='sm'
								variant='ghost'
								color='gray.500'
								onClick={() => setShowConfirm((s) => !s)}
								tabIndex={-1}
								aria-label={showConfirm ? 'Hide password' : 'Show password'}>
								{showConfirm ? <ViewOffIcon /> : <ViewIcon />}
							</Button>
						</InputRightElement>
					</InputGroup>
				</FormControl>

				<Button
					type='submit'
					{...authFormPrimaryButtonProps}
					isLoading={isLoading}
					loadingText='Resetting...'
					isDisabled={!token}>
					Update password
				</Button>

				<Text {...authFormMutedTextProps}>
					Remember your password?{' '}
					<Link
						{...authFormLinkProps}
						cursor='pointer'
						onClick={() => navigate('/auth')}>
						Sign in
					</Link>
				</Text>
			</VStack>
		</Box>
	);
};

export default ResetPassword;
